import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ExamsService } from '../exams.service';
import { LoginService } from '../login.service';
import {
  ClinicExamField,
  controlName,
  fieldTypeMeta,
  normalizeField,
} from './clinic-exam.model';

/**
 * Renders a Clinics exam that was composed in the exam builder (GUS-88).
 *
 * Values live in ClinicExamValues keyed by (ExamID, FieldID, ValueKey) rather
 * than in a column per field, so this mirrors ExamsService.fillForm(): patch
 * silently, then autosave each control on a 500ms debounce.
 */
@Component({
  selector: 'app-clinic-exam',
  templateUrl: './clinic-exam.component.html',
  styleUrls: ['./clinic-exam.component.scss'],
  standalone: false
})
export class ClinicExamComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  fields: ClinicExamField[] = [];
  title = '';
  loaded = false;

  // Auto Text popup state (own popup — the classic one is keyed by a hardcoded DlgID)
  autoTextField: ClinicExamField = null;
  autoTextControl: AbstractControl = null;
  autoTextIsHtml = false;

  private subs = new Subscription();
  private autoSaveWired = false;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '10em',
    placeholder: '',
    defaultParagraphSeparator: 'p',
    sanitize: true,
    toolbarHiddenButtons: [['insertImage', 'insertVideo']],
  };

  constructor(
    public es: ExamsService,
    public ls: LoginService,
    private fb: UntypedFormBuilder
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    // pendingClinicTemplate is only set when the doctor just started this exam
    const starting = this.es.pendingClinicTemplate?.TemplateKind === 'builder';
    // one-shot, like consultation.component does for canvas templates: leaving it
    // set would attach this ClinicTemplateID to the next exam the doctor starts
    this.es.pendingClinicTemplate = null;
    this.loadDefinition(starting);
    // the doctor pulled the patient's earlier exam: the server replaced the values
    this.subs.add(this.es.clinicPrefilled$.subscribe(() => this.loadValues()));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private loadDefinition(starting = false) {
    if (!this.es.examID) return;
    try {
      this.es.getClinicExamDefinition(this.es.examID).subscribe(
        (data: any) => {
          this.title = (data && data.Title) || '';
          this.fields = ((data && data.fields) || []).map((f) => normalizeField(f));
          // name the exam after the template so it is identifiable in the
          // patient's history and on the printout, as canvas templates do
          if (starting && this.title && !this.es.examObject?.Changed) {
            this.es.changeTitle(this.title, true);
          }
          this.buildForm();
          this.loadValues();
        },
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }

  /** One control per value slot the definition needs. */
  private buildForm() {
    const group: any = {};
    for (const f of this.fields) {
      for (const key of this.valueKeys(f)) {
        group[controlName(f, key)] = [''];
      }
    }
    this.form = this.fb.group(group);
  }

  private valueKeys(f: ClinicExamField): string[] {
    switch (f.FieldType) {
      case 'header':
        return [];
      case 'yesno':
        return f.Config.comment ? ['', 'c'] : [''];
      case 'table': {
        const keys: string[] = [];
        (f.Config.rows || []).forEach((r, i) => {
          keys.push('r' + i);
          if (f.Config.comment) keys.push('r' + i + 'c');
        });
        return keys;
      }
      default:
        return [''];
    }
  }

  private loadValues() {
    try {
      this.es.getClinicExamValues(this.es.examID).subscribe(
        (data: any) => {
          const byName: { [name: string]: any } = {};
          for (const row of data || []) {
            byName['f' + row.FieldID + (row.ValueKey ? '_' + row.ValueKey : '')] = row.Value;
          }
          for (const f of this.fields) {
            for (const key of this.valueKeys(f)) {
              const name = controlName(f, key);
              const ctrl = this.form.get(name);
              if (!ctrl) continue;
              // a slot with no stored value is blank (after a pull, the old answer is gone)
              ctrl.setValue(name in byName ? this.coerce(f, key, byName[name]) : '', { emitEvent: false });
            }
          }
          this.wireAutoSave();
          this.loaded = true;
        },
        (err) => { console.log(err); this.wireAutoSave(); this.loaded = true; }
      );
    } catch (e) {
      console.log(e);
      this.loaded = true;
    }
  }

  /**
   * Toggle slots are bound to numeric radio [value]s, but the DB hands every
   * value back as text and Angular's radio accessor compares with ===, so
   * without this a reopened exam would show its toggles blank.
   */
  private coerce(field: ClinicExamField, valueKey: string, raw: any): any {
    if (raw === null || raw === undefined) return '';
    const isToggle =
      (field.FieldType === 'yesno' && valueKey === '') ||
      (field.FieldType === 'table' && /^r\d+$/.test(valueKey));
    if (isToggle && raw !== '' && !isNaN(Number(raw))) return Number(raw);
    return raw;
  }

  private wireAutoSave() {
    // loadValues also runs again after a pull; the controls are already wired
    if (this.autoSaveWired) return;
    this.autoSaveWired = true;
    if (this.es.isClosed) {
      this.form.disable({ emitEvent: false });
      return;
    }
    for (const f of this.fields) {
      for (const key of this.valueKeys(f)) {
        const name = controlName(f, key);
        const ctrl = this.form.get(name);
        if (!ctrl) continue;
        this.subs.add(
          ctrl.valueChanges
            .pipe(debounceTime(500), distinctUntilChanged())
            .subscribe(() => this.saveValue(f, key, ctrl.value))
        );
      }
    }
  }

  private saveValue(field: ClinicExamField, valueKey: string, value: any) {
    try {
      this.es
        .saveClinicExamValue({
          ExamID: this.es.examID,
          FieldID: field.FieldID,
          ValueKey: valueKey,
          Value: value === null || value === undefined ? '' : value,
        })
        .subscribe(
          () => {},
          (err) => { console.log(err); }
        );
    } catch (e) {
      console.log(e);
    }
  }

  // ---------- template helpers ----------

  ctrl(field: ClinicExamField, valueKey = ''): AbstractControl {
    return this.form.get(controlName(field, valueKey));
  }

  name(field: ClinicExamField, valueKey = ''): string {
    return controlName(field, valueKey);
  }

  rowKey(i: number, comment = false): string {
    return 'r' + i + (comment ? 'c' : '');
  }

  hasAutoText(field: ClinicExamField): boolean {
    return fieldTypeMeta(field.FieldType).hasAutoText && (field.autoTexts || []).length > 0;
  }

  /** Double-click clears a field / resets a toggle to -1, per app convention. */
  reset(field: ClinicExamField, valueKey = '', toggle = false) {
    if (this.es.isClosed) return;
    const c = this.ctrl(field, valueKey);
    if (c) c.setValue(toggle ? -1 : '');
  }

  showAutoText(field: ClinicExamField, valueKey = '', isHtml = false) {
    this.autoTextField = field;
    this.autoTextControl = this.ctrl(field, valueKey);
    this.autoTextIsHtml = isHtml;
  }
  closeAutoText() {
    this.autoTextField = null;
    this.autoTextControl = null;
  }

  /** picklist: clicking an item appends it as its own line. */
  appendItem(field: ClinicExamField, content: string) {
    if (this.es.isClosed || !content) return;
    const c = this.ctrl(field);
    if (!c) return;
    const current = c.value;
    c.setValue(current && current !== '' ? current + '\n' + content : content);
  }
}
